
import mapboxgl from 'maplibre-gl';

export default {
  name: 'SGeoMapbox',
  props: {
    accessToken: {
      type: String,
      required: true,
    },
    mapOptions: {
      type: Object,
      required: true,
    },
    navControl: {
      type: Object,
      default: () => ({
        show: true,
        position: 'top-right',
      }),
    },
    geolocateControl: {
      type: Object,
      default: () => ({
        show: false,
        position: 'top-left',
        options: {},
      }),
    },
    scaleControl: {
      type: Object,
      default: () => ({
        show: false,
        position: 'top-left',
        options: {},
      }),
    },
    fullscreenControl: {
      type: Object,
      default: () => ({
        show: false,
        position: 'top-right',
      }),
    },
  },
  data() {
    return {
      options: {},
      geomap: null,
      timeoutId: null,
      resizeObserver: null,
    };
  },
  computed: {
    mapContainer() {
      return this.options.container ? this.options.container : 'map';
    },
  },
  watch: {
    accessToken() {
      if (!this.geomap) return;
      this.geomap.transformRequest = (url) => ({
        url: `${url}?access_token=${this.accessToken}`,
      });
    },
    mapOptions: {
      immediate: true,
      handler(value) {
        this.options = value;
      },
    },
  },
  mounted() {
    // Initialize Map
    this.geomap = this.mapInit();
    if (!this.geomap) return;

    // Add Controls to map
    this.addControls(this.geomap);

    // Register map Events
    this.registerEvents(this.geomap);

    // Resize map
    this.resizeObserver = new ResizeObserver(() => this.resizeMap());
    this.resizeObserver.observe(this.$refs.mapContainer);
  },
  beforeDestroy() {
    if (!this.geomap) return;
    this.geomap.remove();
    clearTimeout(this.timeoutId);
    this.resizeObserver?.unobserve(this.$refs.mapContainer);
  },
  methods: {
    mapInit() {
      // Mapbox GL access token
      // mapboxgl.accessToken = this.accessToken;

      // Add container to options object
      this.options.container = this.mapContainer;

      // Ajout du token d'accès à l'URL
      this.options.transformRequest = (url) => ({
        url: `${url}?access_token=${this.accessToken}`,
      });

      // New Mapbox Instance
      return new mapboxgl.Map(this.options);
    },
    layerMarkers(clusterName, icon) {
      const l = icon.width * icon.left;
      const t = icon.height * -icon.top;

      return {
        id: `${clusterName}-markers`,
        type: 'symbol',
        source: clusterName,
        filter: ['!has', 'point_count'],
        layout: {
          'icon-image': icon.label,
          'icon-offset': [l, t],
        },
      };
    },
    registerEvents(map) {
      // Map Loaded
      map.on('load', () => {
        this.$emit('map-load', map);
      });

      // Map Clicked
      map.on('click', (e) => {
        this.$emit('map-click', map, e);
      });

      // Map cluster-markers Clicked
      map.on('click', 'cluster-markers', (e) => {
        this.$emit('map-click-cluster', e.features[0].properties.id);
      });

      // Map Move End
      map.on('moveend', (e) => {
        this.$emit('map-moveend', map, e);
      });

      // Map Error
      map.on('error', (e) => {
        this.$emit('map-error', map, e);
      });
    },
    addControls(map) {
      // Nav Control
      if (this.navControl.show) {
        const nav = new mapboxgl.NavigationControl();
        map.addControl(nav, this.navControl.position);
      }

      // Geolocation Control
      if (this.geolocateControl.show) {
        const geolocate = new mapboxgl.GeolocateControl(this.geolocateControl.options);
        map.addControl(geolocate, this.geolocateControl.position);

        geolocate.on('geolocate', (position) => {
          this.$emit('geolocate-geolocate', geolocate, position);
        });

        geolocate.on('trackuserlocationstart', () => {
          this.$emit('geolocate-trackuserlocationstart', geolocate);
        });

        geolocate.on('trackuserlocationend', () => {
          this.$emit('geolocate-trackuserlocationend', geolocate);
        });

        geolocate.on('error', (positionError) => {
          this.$emit('geolocate-error', geolocate, positionError);
        });
      }

      // Scale Control
      if (this.scaleControl.show) {
        const scale = new mapboxgl.ScaleControl(this.scaleControl.options);
        map.addControl(scale, this.scaleControl.position);
      }

      // Fullscreen Control
      if (this.fullscreenControl.show) {
        const fullscreen = new mapboxgl.FullscreenControl();
        map.addControl(fullscreen, this.fullscreenControl.position);
      }
    },
    resizeMap() {
      // Wait for the end of window resizing
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.geomap.resize();
      }, 50);
    },
  },
};
